import React, { useState, useRef, useEffect } from 'react'
import { MessageCircle, X, Send, Bot, User, ExternalLink, ChevronDown } from 'lucide-react'
import { knowledgeBase, quickReplies, fallbackResponse, findBestMatch, getResponseById } from '../data/chatbotKnowledge'

export default function ChatBot() {
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState([
    {
      type: 'bot',
      content: `**Kumusta! 👋 I'm LaundryMate Assistant!**

I can answer questions about:
• Pricing - Magkano?
• Features - Ano-ano kasama?
• How to Avail - Paano mag-start?
• SMS & Campaigns

Ano ang gusto mong malaman? 😊`,
      followUp: ['pricing', 'features', 'sms-credits', 'setup']
    }
  ])
  const [input, setInput] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [isOpen])

  // Handle sending a message
  const handleSend = (text = input) => {
    if (!text.trim()) return

    // Add user message
    setMessages(prev => [...prev, { type: 'user', content: text }])
    setInput('')
    setIsTyping(true)

    // Simulate typing delay
    setTimeout(() => {
      const match = findBestMatch(text)

      if (match) {
        setMessages(prev => [...prev, {
          type: 'bot',
          content: match.answer,
          followUp: match.followUp
        }])
      } else {
        setMessages(prev => [...prev, {
          type: 'bot',
          content: fallbackResponse.answer,
          followUp: fallbackResponse.followUp
        }])
      }
      setIsTyping(false)
    }, 500 + Math.random() * 500)
  }

  // Handle quick reply click
  const handleQuickReply = (id) => {
    const response = getResponseById(id)
    if (response) {
      setMessages(prev => [...prev, { type: 'user', content: response.question }])
      setIsTyping(true
      )
      setTimeout(() => {
        setMessages(prev => [...prev, {
          type: 'bot',
          content: response.answer,
          followUp: response.followUp
        }])
        setIsTyping(false)
      }, 500)
    }
  }

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  // Format message content (basic markdown)
  const formatMessage = (content) => {
    return content
      .split('\n')
      .map((line, i) => {
        // Bold text
        line = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        // Check/X marks
        line = line.replace(/^✅/g, '<span class="text-green-500">✅</span>')
        line = line.replace(/^❌/g, '<span class="text-red-500">❌</span>')
        // Bullet points
        if (line.startsWith('•') || line.startsWith('-')) {
          return `<div class="ml-2">${line}</div>`
        }
        return line
      })
      .join('<br />')
  }

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-50 bg-gradient-to-r from-cyan-500 to-cyan-600 text-white p-4 rounded-full shadow-2xl hover:shadow-cyan-500/25 hover:scale-110 transition-all duration-300 ${isOpen ? 'hidden' : 'flex'} items-center gap-2 group`}
      >
        <MessageCircle className="w-6 h-6" />
        <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 whitespace-nowrap">
          Chat with us
        </span>
        {/* Ping animation */}
        <span className="absolute top-0 right-0 flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-3 w-3 bg-yellow-400"></span>
        </span>
      </button>

      {/* Chat Window */}
      <div className={`fixed bottom-6 right-6 z-50 transition-all duration-300 ${isOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'}`}>
        <div className="w-[380px] max-w-[calc(100vw-2rem)] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col" style={{ height: '600px', maxHeight: 'calc(100vh - 100px)' }}>

          {/* Header */}
          <div className="bg-gradient-to-r from-cyan-500 to-cyan-600 p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Bot className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-bold">LaundryMate Assistant</h3>
                <p className="text-cyan-100 text-xs flex items-center gap-1">
                  <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                  Online now
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white/80 hover:text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-gray-800">
            {messages.map((message, index) => (
              <div key={index} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`flex gap-2 max-w-[85%] ${message.type === 'user' ? 'flex-row-reverse' : ''}`}>
                  {/* Avatar */}
                  <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center ${
                    message.type === 'user'
                      ? 'bg-cyan-500 text-white'
                      : 'bg-gradient-to-br from-cyan-400 to-cyan-600 text-white'
                  }`}>
                    {message.type === 'user' ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                  </div>

                  {/* Message Bubble */}
                  <div className={`rounded-2xl px-4 py-3 ${
                    message.type === 'user'
                      ? 'bg-cyan-500 text-white rounded-tr-none'
                      : 'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-tl-none shadow-sm border border-gray-100 dark:border-gray-600'
                  }`}>
                    <div
                      className="text-sm leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: formatMessage(message.content) }}
                    />

                    {/* Follow-up Quick Replies */}
                    {message.type === 'bot' && message.followUp && (
                      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100 dark:border-gray-600">
                        {message.followUp.map(id => {
                          const reply = quickReplies.find(r => r.id === id) || getResponseById(id)
                          if (!reply) return null
                          return (
                            <button
                              key={id}
                              onClick={() => handleQuickReply(id)}
                              className="text-xs px-3 py-1.5 bg-cyan-50 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400 rounded-full hover:bg-cyan-100 dark:hover:bg-cyan-900/50 transition-colors"
                            >
                              {reply.label || reply.question?.slice(0, 20) + '...'}
                            </button>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* Typing Indicator */}
            {isTyping && (
              <div className="flex justify-start">
                <div className="flex gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                  <div className="bg-white dark:bg-gray-700 rounded-2xl rounded-tl-none px-4 py-3 shadow-sm border border-gray-100 dark:border-gray-600">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Quick Replies (Initial) */}
          {messages.length === 1 && (
            <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Quick questions:</p>
              <div className="flex flex-wrap gap-2">
                {quickReplies.map(reply => (
                  <button
                    key={reply.id}
                    onClick={() => handleQuickReply(reply.id)}
                    className="text-sm px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full border border-gray-200 dark:border-gray-600 hover:border-cyan-500 hover:text-cyan-600 transition-colors"
                  >
                    {reply.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-700">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your question..."
                className="flex-1 px-4 py-3 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-800 dark:text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm"
              />
              <button
                onClick={() => handleSend()}
                disabled={!input.trim()}
                className="px-4 py-3 bg-gradient-to-r from-cyan-500 to-cyan-600 text-white rounded-xl hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>

            {/* Human Support Link */}
            <a
              href="https://m.me/946243658562372"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 mt-3 text-xs text-gray-500 dark:text-gray-400 hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors"
            >
              <ExternalLink className="w-3 h-3" />
              Chat with a human on Messenger
            </a>
          </div>
        </div>
      </div>
    </>
  )
}
